<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">串题组管理</h1>
      <button @click="handleCreate"
        class="primary-btn flex items-center gap-2">
        <PlusIcon class="w-5 h-5" />
        新建串题组
      </button>
    </div>

    <!-- 筛选器 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div class="flex flex-wrap gap-4">
        <!-- 分类筛选 -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">分类:</label>
          <select v-model="filterForm.categoryId" @change="handleFilter"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            <option :value="undefined">全部分类</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 串题组列表 -->
    <div v-else-if="topicGroups.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              ID</th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              组名</th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              分类</th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              话题数</th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              答案</th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              状态</th>
            <th
              class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              操作</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="group in topicGroups" :key="group.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ group.id }}</td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ group.name }}</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {{ getCategoryName(group.categoryId) }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ group.memberCount }} 题
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span v-if="group.hasAnswer" class="text-green-600 dark:text-green-400">✅ 已添加</span>
              <span v-else class="text-gray-400 dark:text-gray-500">❌ 未添加</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span v-if="group.isVisible"
                class="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                显示
              </span>
              <span v-else
                class="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                隐藏
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button @click="handleEdit(group)"
                class="link-btn-edit mr-3">
                编辑
              </button>
              <button @click="handleDelete(group)"
                class="link-btn-delete">
                删除
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 空状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <div class="text-gray-400 dark:text-gray-500 mb-4">
        <LinkIcon class="mx-auto h-12 w-12" />
      </div>
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">暂无串题组</p>
      <p class="text-sm text-gray-500 dark:text-gray-400">点击右上角"新建串题组"按钮创建第一个串题组</p>
    </div>

    <!-- 分页 -->
    <Pagination v-if="pagination.total > 0"
      :total="pagination.total"
      v-model:current-page="pagination.page"
      v-model:page-size="pagination.limit"
      :show-jumper="false"
      @update:current-page="loadTopicGroups"
      @update:page-size="loadTopicGroups"
    />

    <!-- 删除确认模态框 -->
    <ConfirmModal ref="confirmModalRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { PlusIcon, LinkIcon } from '@heroicons/vue/24/outline'
import Pagination from '@/components/ui/Pagination.vue'
import { getTopicGroupsAPI, deleteTopicGroupAPI } from '@/api/speaking/topicGroup'
import { fetchCategoriesAPI } from '@/api/speaking/topic'
import type { TopicGroupListItem } from '@/types/topic/topicGroup'
import type { Category } from '@/types/topic/topic'
import toaster from '@/utils/toaster'
import ConfirmModal from '@/components/ui/ConfirmModal.vue'

const router = useRouter()

// 状态
const loading = ref(false)
const topicGroups = ref<TopicGroupListItem[]>([])
const categories = ref<Category[]>([])
const pagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
})
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null)

// 筛选表单
const filterForm = reactive({
  categoryId: undefined as number | undefined,
})

/**
 * 加载分类列表
 */
const loadCategories = async () => {
  try {
    const response = await fetchCategoriesAPI()
    if (response.data) {
      categories.value = response.data
    }
  } catch (error) {
    console.error('加载分类列表失败:', error)
  }
}

/**
 * 加载串题组列表
 */
const loadTopicGroups = async () => {
  loading.value = true
  try {
    const response = await getTopicGroupsAPI({
      page: pagination.page,
      limit: pagination.limit,
      categoryId: filterForm.categoryId,
    })

    if (response.data) {
      topicGroups.value = response.data.items
      pagination.total = response.data.total
    }
  } catch (error) {
    toaster.error('加载串题组列表失败')
    console.error('加载串题组列表失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 获取分类名称
 */
const getCategoryName = (categoryId: number): string => {
  const category = categories.value.find(c => c.id === categoryId)
  return category?.name || '未知分类'
}

/**
 * 处理筛选
 */
const handleFilter = () => {
  pagination.page = 1
  loadTopicGroups()
}

/**
 * 处理新建串题组
 */
const handleCreate = () => {
  router.push('/topic-group/create')
}

/**
 * 处理编辑串题组
 */
const handleEdit = (group: TopicGroupListItem) => {
  router.push(`/topic-group/${group.id}/edit`)
}

/**
 * 处理删除串题组
 */
const handleDelete = async (group: TopicGroupListItem) => {
  if (!confirmModalRef.value) return

  try {
    const confirmed = await confirmModalRef.value.confirm(
      `确定要删除串题组"${group.name}"吗？<br><br><span style="color: #ef4444;">此操作将同时删除所有关联的成员、答案和微调，且不可恢复</span>`,
      '确认删除'
    )

    if (!confirmed) return

    await deleteTopicGroupAPI(group.id)
    toaster.success('删除成功')
    loadTopicGroups()
  } catch (error) {
    toaster.error('删除失败')
    console.error('删除串题组失败:', error)
  }
}

// 组件挂载时加载数据
onMounted(async () => {
  await loadCategories()
  await loadTopicGroups()
})
</script>
